import {SearchConditionElement} from "./SearchConditionElement";
import {SortOrderElement} from "./SortOrderElement";
import {HttpClient, httpClient} from "./HttpClient";

export type DefaultPostIO<VO> = <DTO>(dto: DTO) => Promise<VO>

export type DefaultDeleteIO = (id: bigint) => Promise<void>

export type DefaultDeleteIdsIO = (ids: bigint[]) => Promise<void>

export type DefaultGetIO<VO> = (id: bigint) => Promise<VO>

export type DefaultListIO<VO, Table = VO> = (
    filter: SearchConditionElement<Table>[],
    order: SortOrderElement<Table>[],
    size: number,
    offset: bigint
) => Promise<VO[]>

export type DefaultCountIO<Table> = (filter: SearchConditionElement<Table>[]) => Promise<bigint>

export type DefaultIO<VO, Table = VO> = {
    post: DefaultPostIO<VO>,
    put<DTO>(id: bigint, dto: DTO): Promise<VO>,
    patch<DTO>(id: bigint, dto: DTO): Promise<VO>,
    delete: DefaultDeleteIO,
    get: DefaultGetIO<VO>,
    getFirst(filter: SearchConditionElement<Table>[], order: SortOrderElement<Table>[]): Promise<VO>,
    list: DefaultListIO<VO, Table>,
    count: DefaultCountIO<Table>,
    deleteIds(ids: bigint[]): Promise<void>,
}

export function defaultIO<VO, Table = VO>(
    path: string,
    construct: (o: any) => VO,
    override?: Partial<DefaultIO<VO, Table>>
): DefaultIO<VO, Table> {
    return {
        post: override?.post ?? (<DTO>(dto: DTO): Promise<VO> => httpClient.post(
            path,
            dto,
            construct
        )),
        put: override?.put ?? (<DTO>(id: bigint, dto: DTO): Promise<VO> => httpClient.put(
            `${path}/${id}`,
            dto,
            construct
        )),
        delete: override?.delete ?? ((id: bigint): Promise<void> => httpClient.delete(
            `${path}/${id}`
        )),
        get: override?.get ?? ((id: bigint): Promise<VO> => httpClient.get(
            `${path}/${id}`,
            construct
        )),
        getFirst: override?.getFirst ?? ((filter: SearchConditionElement<Table>[], order: SortOrderElement<Table>[]): Promise<VO> => httpClient.get(
            HttpClient.buildUrl(path, filter, order),
            construct
        )),
        list: override?.list ?? ((filter: SearchConditionElement<Table>[], order: SortOrderElement<Table>[], size: number, offset: bigint): Promise<VO[]> => httpClient.get(
            HttpClient.buildUrl(path, filter, order, size, offset),
            o => (o as any[]).map(construct)
        )),
        count: override?.count ?? ((filter: SearchConditionElement<Table>[]): Promise<bigint> => httpClient.get(
            HttpClient.buildUrl(`${path}/count`, filter),
            BigInt
        )),
        patch: override?.patch ?? (<DTO>(id: bigint, dto: DTO): Promise<VO> => httpClient.patch(
            `${path}/${id}`,
            dto,
            construct
        )),
        deleteIds: override?.deleteIds ?? ((ids: bigint[]): Promise<void> => httpClient.deleteIds(
            `${path}`,
             ids,
        )),
    }
}